/**
 * Test ID Constants and Utilities
 * 
 * Centralized test IDs for Cursor browser automation testing.
 * 
 * Naming Convention: {module}-{component}-{element}-{qualifier?}
 * 
 * Examples:
 * - auth-login-phone-input
 * - chartmind-recording-start-btn
 * - patient-cmr-qr-display
 * 
 * Usage:
 * ```jsx
 * import { TEST_IDS } from 'utils/testIds';
 * <Button data-testid={TEST_IDS.auth.loginSubmitBtn}>Login</Button>
 * ```
 */

// ============================================================================
// AUTHENTICATION MODULE
// ============================================================================
export const AUTH_TEST_IDS = {
  // Login Page
  loginPage: 'auth-login-page',
  phoneInput: 'auth-login-phone-input',
  submitBtn: 'auth-login-submit-btn',
  errorMessage: 'auth-login-error',
  successMessage: 'auth-login-success',
  
  // Verification
  codeInput: 'auth-verify-code-input',
  codeInputDigit: (index) => `auth-verify-code-digit-${index}`,
  verifyBtn: 'auth-verify-submit-btn',
  resendBtn: 'auth-verify-resend-btn',
  
  // Dev Login
  devLoginPage: 'auth-dev-login-page',
  devRoleBtn: (role) => `auth-dev-role-${role}-btn`,
  devBackBtn: 'auth-dev-back-btn',
  
  // Registration
  registerPage: 'auth-register-page',
  registerNameInput: 'auth-register-name-input',
  registerEmailInput: 'auth-register-email-input',
  registerSubmitBtn: 'auth-register-submit-btn',
  
  // Password Reset
  resetPage: 'auth-reset-page',
  resetEmailInput: 'auth-reset-email-input',
  resetSubmitBtn: 'auth-reset-submit-btn',
};

// ============================================================================
// CHARTMIND MODULE
// ============================================================================
export const CHARTMIND_TEST_IDS = {
  // Dashboard
  dashboardPage: 'chartmind-dashboard-page',
  newEncounterBtn: 'chartmind-dashboard-new-encounter-btn',
  historyBtn: 'chartmind-dashboard-history-btn',
  settingsBtn: 'chartmind-dashboard-settings-btn',
  noteTypesBtn: 'chartmind-dashboard-note-types-btn',
  recentEncountersSection: 'chartmind-dashboard-recent-encounters',
  encounterCard: (index) => `chartmind-dashboard-encounter-${index}`,
  startRecordingBtn: 'chartmind-dashboard-start-recording-btn',
  
  // Recording Screen
  recordingPage: 'chartmind-recording-page',
  noteTypeSelect: 'chartmind-recording-note-type-select',
  noteTypeOption: (id) => `chartmind-recording-note-type-${id}`,
  recordStartBtn: 'chartmind-recording-start-btn',
  stopRecordingBtn: 'chartmind-recording-stop-btn',
  pauseRecordingBtn: 'chartmind-recording-pause-btn',
  timerDisplay: 'chartmind-recording-timer',
  generateChartBtn: 'chartmind-recording-generate-btn',
  saveDraftBtn: 'chartmind-recording-save-draft-btn',
  
  // Card Navigation
  cardContainer: 'chartmind-cards-container',
  cardIndicator: (cardId) => `chartmind-card-indicator-${cardId}`,
  cardPanel: (cardId) => `chartmind-card-panel-${cardId}`,
  
  // Patient Card
  patientCard: 'chartmind-card-patient',
  patientLinkBtn: 'chartmind-patient-link-btn',
  patientScanQRBtn: 'chartmind-patient-scan-qr-btn',
  patientScanIDBtn: 'chartmind-patient-scan-id-btn',
  patientUnlinkBtn: 'chartmind-patient-unlink-btn',
  patientName: 'chartmind-patient-name',
  patientDOB: 'chartmind-patient-dob',
  
  // DDX Card
  ddxCard: 'chartmind-card-ddx',
  ddxDiagnosis: (index) => `chartmind-ddx-diagnosis-${index}`,
  ddxSelectBtn: (index) => `chartmind-ddx-select-${index}`,
  ddxVisualization: 'chartmind-ddx-visualization',
  ddxViewToggle: 'chartmind-ddx-view-toggle',
  
  // Questions Card
  questionsCard: 'chartmind-card-questions',
  questionItem: (index) => `chartmind-question-${index}`,
  redFlagItem: (index) => `chartmind-red-flag-${index}`,
  
  // Tests Card
  testsCard: 'chartmind-card-tests',
  testItem: (index) => `chartmind-test-${index}`,
  testSelectCheckbox: (index) => `chartmind-test-select-${index}`,
  testsViewToggle: 'chartmind-tests-view-toggle',
  
  // Treatment Card
  treatmentCard: 'chartmind-card-treatment',
  treatmentItem: (index) => `chartmind-treatment-${index}`,
  treatmentSelectCheckbox: (index) => `chartmind-treatment-select-${index}`,
  treatmentViewToggle: 'chartmind-treatment-view-toggle',
  
  // Discharge Card
  dischargeCard: 'chartmind-card-discharge',
  dischargeGenerateBtn: 'chartmind-discharge-generate-btn',
  dischargeDiagnosisInput: 'chartmind-discharge-diagnosis-input',
  dischargeMedicationItem: (index) => `chartmind-discharge-med-${index}`,
  dischargeFollowUpInput: 'chartmind-discharge-followup-input',
  dischargeWarningItem: (index) => `chartmind-discharge-warning-${index}`,
  
  // Transcript Card
  transcriptCard: 'chartmind-card-transcript',
  transcriptText: 'chartmind-transcript-text',
  transcriptSegment: (index) => `chartmind-transcript-segment-${index}`,
  
  // Language/Translation
  providerLanguageSelect: 'chartmind-provider-language-select',
  patientLanguageSelect: 'chartmind-patient-language-select',
  speakerToggleBtn: 'chartmind-speaker-toggle-btn',
  translationStatus: 'chartmind-translation-status',
  
  // Chart Generation
  chartGenerationPage: 'chartmind-chart-generation-page',
  chartPreview: 'chartmind-chart-preview',
  chartSectionTab: (section) => `chartmind-chart-section-${section}`,
  chartSaveBtn: 'chartmind-chart-save-btn',
  chartCopyBtn: 'chartmind-chart-copy-btn',
  chartEditBtn: 'chartmind-chart-edit-btn',
  
  // History
  historyPage: 'chartmind-history-page',
  historyTabInProgress: 'chartmind-history-tab-in-progress',
  historyTabCompleted: 'chartmind-history-tab-completed',
  historySearchInput: 'chartmind-history-search-input',
  historyEncounterItem: (index) => `chartmind-history-encounter-${index}`,
  historyResumeBtn: (id) => `chartmind-history-resume-${id}`,
  historyViewBtn: (id) => `chartmind-history-view-${id}`,
  
  // Encounter View
  encounterViewPage: 'chartmind-encounter-view-page',
  encounterAddAddendumBtn: 'chartmind-encounter-add-addendum-btn',
  encounterEditBtn: 'chartmind-encounter-edit-btn',
  encounterCopyBtn: 'chartmind-encounter-copy-btn',
  
  // Note Types / Templates
  noteTypesPage: 'chartmind-note-types-page',
  noteTypeCard: (id) => `chartmind-note-type-${id}`,
  noteTypeCreateBtn: 'chartmind-note-type-create-btn',
  noteTypeEditBtn: (id) => `chartmind-note-type-edit-${id}`,
  noteTypeDeleteBtn: (id) => `chartmind-note-type-delete-${id}`,
  
  // Template Editor
  templateEditorPage: 'chartmind-template-editor-page',
  templateNameInput: 'chartmind-template-name-input',
  templateDescInput: 'chartmind-template-desc-input',
  templateSaveBtn: 'chartmind-template-save-btn',
  templateSaveAsBtn: 'chartmind-template-save-as-btn',
  templateCardCheckbox: (cardId) => `chartmind-template-card-${cardId}`,
  templatePromptAccordion: (cardId) => `chartmind-template-prompt-${cardId}`,
  templateAssignmentToggle: 'chartmind-template-assignment-toggle',
  templateEmailInput: 'chartmind-template-email-input',
  templateEmailAddBtn: 'chartmind-template-email-add-btn',
  templateEmailChip: (email) => `chartmind-template-email-${email.replace('@', '-at-')}`,
  
  // Settings
  settingsPage: 'chartmind-settings-page',
  
  // CMR Scanner
  cmrScannerDialog: 'chartmind-cmr-scanner-dialog',
  cmrScannerVideo: 'chartmind-cmr-scanner-video',
  cmrScannerCloseBtn: 'chartmind-cmr-scanner-close-btn',
  
  // ID Scanner
  idScannerDialog: 'chartmind-id-scanner-dialog',
  idScannerVideo: 'chartmind-id-scanner-video',
  idScannerCaptureBtn: 'chartmind-id-scanner-capture-btn',
  idScannerConfirmBtn: 'chartmind-id-scanner-confirm-btn',
  idScannerRetakeBtn: 'chartmind-id-scanner-retake-btn',
};

// ============================================================================
// PATIENT/INDIVIDUAL MODULE
// ============================================================================
export const PATIENT_TEST_IDS = {
  // Dashboard
  dashboardPage: 'patient-dashboard-page',
  symptomsBtn: 'patient-dashboard-symptoms-btn',
  medicationsBtn: 'patient-dashboard-medications-btn',
  recordsBtn: 'patient-dashboard-records-btn',
  
  // CMR / SecureTransfer
  cmrPage: 'patient-cmr-page',
  cmrQRDisplay: 'patient-cmr-qr-display',
  cmrQRCode: 'patient-cmr-qr-code',
  cmrQRTimer: 'patient-cmr-qr-timer',
  cmrShareBtn: 'patient-cmr-share-btn',
  cmrGenerateQRBtn: 'patient-cmr-generate-qr-btn',
  cmrRefreshQRBtn: 'patient-cmr-refresh-qr-btn',
  cmrShareNameToggle: 'patient-cmr-share-name-toggle',
  cmrShareDobToggle: 'patient-cmr-share-dob-toggle',
  cmrInfoBtn: 'patient-cmr-info-btn',
  
  // SecureTransfer (cross-role mock testing)
  secureTransferToken: 'patient-secure-transfer-token',
  secureTransferCopyBtn: 'patient-secure-transfer-copy-btn',
  
  // Discharge Plans
  dischargePlansPage: 'patient-discharge-plans-page',
  dischargePlanItem: (index) => `patient-discharge-plan-${index}`,
  dischargePlanViewBtn: (id) => `patient-discharge-plan-view-${id}`,
  
  // Medications
  medicationsPage: 'patient-medications-page',
  medicationItem: (index) => `patient-medication-${index}`,
  medicationAddBtn: 'patient-medication-add-btn',
  medicationEditBtn: (id) => `patient-medication-edit-${id}`,
  medicationTrackingBtn: 'patient-medication-tracking-btn',
};

// ============================================================================
// SYMPTOM CHECK MODULE
// ============================================================================
export const SYMPTOM_TEST_IDS = {
  // Main Page
  symptomCheckPage: 'symptom-check-page',
  startBtn: 'symptom-check-start-btn',
  
  // Chat Interface
  chatContainer: 'symptom-chat-container',
  chatInput: 'symptom-chat-input',
  chatSendBtn: 'symptom-chat-send-btn',
  chatMessage: (index) => `symptom-chat-message-${index}`,
  chatVoiceBtn: 'symptom-chat-voice-btn',
  
  // DDX Display
  ddxContainer: 'symptom-ddx-container',
  ddxItem: (index) => `symptom-ddx-item-${index}`,
  ddxUrgencyBadge: 'symptom-ddx-urgency',
  
  // Actions
  emergencyBtn: 'symptom-emergency-btn',
  findCareBtn: 'symptom-find-care-btn',
  saveResultsBtn: 'symptom-save-results-btn',
};

// ============================================================================
// NAVIGATION MODULE
// ============================================================================
export const NAV_TEST_IDS = {
  // Desktop Navigation
  dashboardContainer: 'nav-dashboard-container',
  appBar: 'nav-app-bar',
  sidebar: 'nav-sidebar',
  sidebarCollapseBtn: 'nav-sidebar-collapse-btn',
  mainContent: 'nav-main-content',
  mobileMenuBtn: 'nav-mobile-menu-btn',
  
  // Bottom Navigation (iOS)
  bottomNav: 'nav-bottom-container',
  bottomNavHome: 'nav-bottom-home-btn',
  bottomNavHistory: 'nav-bottom-history-btn',
  bottomNavSettings: 'nav-bottom-settings-btn',
  bottomNavProfile: 'nav-bottom-profile-btn',
  
  // Drawer Navigation
  drawer: 'nav-drawer-container',
  drawerItem: (id) => `nav-drawer-item-${id}`,
  drawerLogoutBtn: 'nav-drawer-logout-btn',
  
  // Header
  header: 'nav-header-container',
  headerBackBtn: 'nav-header-back-btn',
  headerMenuBtn: 'nav-header-menu-btn',
  headerTitle: 'nav-header-title',
  
  // Profile Menu
  profileMenu: 'nav-profile-menu',
  profileLogoutBtn: 'nav-profile-logout-btn',
  profileSettingsBtn: 'nav-profile-settings-btn',
};

// ============================================================================
// DASHBOARD MODULE
// ============================================================================
export const DASHBOARD_TEST_IDS = {
  // Layout
  dashboardLayout: 'dashboard-layout',
  dashboardContent: 'dashboard-content',
  
  // Settings
  settingsPage: 'dashboard-settings-page',
  settingsPhoneInput: 'dashboard-settings-phone-input',
  settingsSaveBtn: 'dashboard-settings-save-btn',
  
  // Account
  accountPage: 'dashboard-account-page',
  accountNameInput: 'dashboard-account-name-input',
  accountEmailInput: 'dashboard-account-email-input',
  accountSaveBtn: 'dashboard-account-save-btn',
  
  // Upgrade
  upgradePage: 'dashboard-upgrade-page',
  upgradePlanCard: (plan) => `dashboard-upgrade-plan-${plan}`,
  upgradeSelectBtn: (plan) => `dashboard-upgrade-select-${plan}`,
};

// ============================================================================
// FACILITY MODULE
// ============================================================================
export const FACILITY_TEST_IDS = {
  dashboardPage: 'facility-dashboard-page',
  queuePage: 'facility-queue-page',
  schedulerPage: 'facility-scheduler-page',
  statusPage: 'facility-status-page',
  
  // Alerts
  alertsPanel: 'facility-alerts-panel',
  alertItem: (index) => `facility-alert-${index}`,
  alertToggle: 'facility-alert-toggle',
  alertTimeChip: (index) => `facility-alert-time-${index}`,
  alertAddTimeBtn: 'facility-alert-add-time-btn',
  
  // Checklist
  checklistPanel: 'facility-checklist-panel',
  checklistItem: (id) => `facility-checklist-item-${id}`,
  checklistDismissBtn: 'facility-checklist-dismiss-btn',
  checklistProgressBar: 'facility-checklist-progress',
  
  // Onboarding Dialog
  onboardingDialog: 'facility-onboarding-dialog',
  onboardingStep: (step) => `facility-onboarding-step-${step}`,
  onboardingNextBtn: 'facility-onboarding-next-btn',
  onboardingDismissBtn: 'facility-onboarding-dismiss-btn',
  
  // Wait Time
  waitTimeDisplay: 'facility-wait-time-display',
  waitTimeUpdateBtn: 'facility-wait-time-update-btn',
  waitTimeInput: 'facility-wait-time-input',
  waitTimeSaveBtn: 'facility-wait-time-save-btn',
};

// ============================================================================
// MAP MODULE
// ============================================================================
export const MAP_TEST_IDS = {
  // Main Map
  mapContainer: 'map-container',
  mapCanvas: 'map-canvas',
  
  // Header
  header: 'map-header',
  searchInput: 'map-search-input',
  searchBtn: 'map-search-btn',
  filterBtn: 'map-filter-btn',
  authBtn: 'map-auth-btn',
  
  // Filters
  filterPanel: 'map-filter-panel',
  filterFacilityType: 'map-filter-facility-type',
  filterRating: 'map-filter-rating',
  filterApplyBtn: 'map-filter-apply-btn',
  filterClearBtn: 'map-filter-clear-btn',
  
  // Markers
  marker: (id) => `map-marker-${id}`,
  markerCluster: (count) => `map-marker-cluster-${count}`,
  
  // Location Modal
  locationModal: 'map-location-modal',
  locationModalTitle: 'map-location-modal-title',
  locationModalAddress: 'map-location-modal-address',
  locationModalPhone: 'map-location-modal-phone',
  locationModalDirectionsBtn: 'map-location-modal-directions-btn',
  locationModalCloseBtn: 'map-location-modal-close-btn',
  locationModalRating: 'map-location-modal-rating',
  locationModalWaitTime: 'map-location-modal-wait-time',
  locationModalFeatures: 'map-location-modal-features',
  
  // Ad Carousel
  adCarousel: 'map-ad-carousel',
  adCarouselItem: (index) => `map-ad-carousel-item-${index}`,
  adCarouselPrevBtn: 'map-ad-carousel-prev-btn',
  adCarouselNextBtn: 'map-ad-carousel-next-btn',
  
  // Welcome Dialog
  welcomeDialog: 'map-welcome-dialog',
  welcomeLocationInput: 'map-welcome-location-input',
  welcomeSubmitBtn: 'map-welcome-submit-btn',
  welcomeUseCurrentBtn: 'map-welcome-use-current-btn',
  
  // Legend
  legendPanel: 'map-legend-panel',
  legendItem: (type) => `map-legend-item-${type}`,
};

// ============================================================================
// ADMIN MODULE
// ============================================================================
export const ADMIN_TEST_IDS = {
  // ChartMind Admin
  chartmindAdminPage: 'admin-chartmind-page',
  designeeManagerPage: 'admin-designee-manager-page',
  designeeAddBtn: 'admin-designee-add-btn',
  designeeEmailInput: 'admin-designee-email-input',
  designeeItem: (email) => `admin-designee-${email.replace('@', '-at-')}`,
  designeeRemoveBtn: (email) => `admin-designee-remove-${email.replace('@', '-at-')}`,
  
  // System Admin Dashboard
  systemAdminPage: 'admin-system-page',
  systemAdminRefreshBtn: 'admin-system-refresh-btn',
  systemAdminTab: (index) => `admin-system-tab-${index}`,
  systemAdminOverviewCard: (title) => `admin-system-overview-${title}`,
  systemAdminFeatureToggle: (flag) => `admin-system-feature-${flag}`,
  systemAdminEpidemiologyCard: (region) => `admin-system-epi-${region}`,
  systemAdminHealthStatus: (service) => `admin-system-health-${service}`,
  
  // MSI3 Admin Dashboard
  msi3AdminPage: 'admin-msi3-page',
  msi3RefreshBtn: 'admin-msi3-refresh-btn',
  msi3ExportBtn: 'admin-msi3-export-btn',
  msi3Tab: (index) => `admin-msi3-tab-${index}`,
  msi3StatCard: (stat) => `admin-msi3-stat-${stat}`,
  msi3CitiesTable: 'admin-msi3-cities-table',
  msi3CityRow: (id) => `admin-msi3-city-${id}`,
  msi3CityEditBtn: (id) => `admin-msi3-city-edit-${id}`,
  msi3FacilitiesTable: 'admin-msi3-facilities-table',
  msi3FacilityRow: (id) => `admin-msi3-facility-${id}`,
  msi3EnrollmentsTable: 'admin-msi3-enrollments-table',
  msi3EnrollmentRow: (id) => `admin-msi3-enrollment-${id}`,
  msi3OutOfMarketTable: 'admin-msi3-out-of-market-table',
  msi3SeedCitiesBtn: 'admin-msi3-seed-cities-btn',
  msi3AddCityBtn: 'admin-msi3-add-city-btn',
  msi3CityDialog: 'admin-msi3-city-dialog',
  
  // User Invitations
  invitationsPage: 'admin-invitations-page',
  invitationNameInput: 'admin-invitation-name-input',
  invitationEmailInput: 'admin-invitation-email-input',
  invitationRoleSelect: 'admin-invitation-role-select',
  invitationCitySelect: 'admin-invitation-city-select',
  invitationNotesInput: 'admin-invitation-notes-input',
  invitationSendBtn: 'admin-invitation-send-btn',
  invitationsTable: 'admin-invitations-table',
  invitationRow: (id) => `admin-invitation-${id}`,
  invitationCopyBtn: (id) => `admin-invitation-copy-${id}`,
  invitationResendBtn: (id) => `admin-invitation-resend-${id}`,
  invitationRevokeBtn: (id) => `admin-invitation-revoke-${id}`,
  invitationRefreshBtn: 'admin-invitation-refresh-btn',
  
  // Location Approval
  approvalPage: 'admin-approval-page',
  approvalTable: 'admin-approval-table',
  approvalRow: (id) => `admin-approval-row-${id}`,
  approvalCheckbox: (id) => `admin-approval-checkbox-${id}`,
  approvalSelectAllCheckbox: 'admin-approval-select-all',
  approvalApproveBtn: 'admin-approval-approve-btn',
  approvalRejectBtn: 'admin-approval-reject-btn',
  
  // Usage Monitor
  usageMonitorPage: 'admin-usage-monitor-page',
  usageMonthSelect: 'admin-usage-month-select',
  usageStatCard: (stat) => `admin-usage-stat-${stat}`,
  usageSourceTable: 'admin-usage-source-table',
  usageModelTable: 'admin-usage-model-table',
  usageUserTable: 'admin-usage-user-table',
  
  // Symptom Check Settings
  symptomSettingsPage: 'admin-symptom-settings-page',
  symptomSettingsSaveBtn: 'admin-symptom-settings-save-btn',
  symptomSettingsEnabledToggle: 'admin-symptom-enabled-toggle',
  symptomSettingsVisualizationToggle: 'admin-symptom-visualization-toggle',
  symptomSettingsQuestionsToggle: 'admin-symptom-questions-toggle',
  symptomSettingsHighlightToggle: 'admin-symptom-highlight-toggle',
  symptomSettingsModelSelect: 'admin-symptom-model-select',
  symptomSettingsMaxTokensInput: 'admin-symptom-max-tokens-input',
  symptomSettingsTemperatureInput: 'admin-symptom-temperature-input',
  symptomSettingsDisclaimerInput: 'admin-symptom-disclaimer-input',
  
  // City Manager
  cityManagerPage: 'admin-city-manager-page',
  cityManagerCitySelect: 'admin-city-manager-city-select',
  cityManagerStatsCard: (stat) => `admin-city-manager-stat-${stat}`,
  
  // Assistant Manager / MSI Localizer
  assistantManagerPage: 'admin-assistant-manager-page',
  assistantConfigTab: 'admin-assistant-config-tab',
  assistantLocalizationTab: 'admin-assistant-localization-tab',
  assistantPromptTab: 'admin-assistant-prompt-tab',
  assistantRAGTab: 'admin-assistant-rag-tab',
};

// ============================================================================
// COMMON/SHARED
// ============================================================================
export const COMMON_TEST_IDS = {
  // Loading States
  loadingSpinner: 'common-loading-spinner',
  loadingOverlay: 'common-loading-overlay',
  
  // Dialogs
  dialog: 'common-dialog',
  dialogTitle: 'common-dialog-title',
  dialogCloseBtn: 'common-dialog-close-btn',
  dialogConfirmBtn: 'common-dialog-confirm-btn',
  dialogCancelBtn: 'common-dialog-cancel-btn',
  
  // Alerts/Snackbars
  alert: 'common-alert',
  alertCloseBtn: 'common-alert-close-btn',
  snackbar: 'common-snackbar',
  
  // Forms
  formError: 'common-form-error',
  formSuccess: 'common-form-success',
};

// ============================================================================
// COMBINED EXPORT
// ============================================================================
export const TEST_IDS = {
  auth: AUTH_TEST_IDS,
  chartmind: CHARTMIND_TEST_IDS,
  patient: PATIENT_TEST_IDS,
  symptom: SYMPTOM_TEST_IDS,
  nav: NAV_TEST_IDS,
  dashboard: DASHBOARD_TEST_IDS,
  facility: FACILITY_TEST_IDS,
  map: MAP_TEST_IDS,
  admin: ADMIN_TEST_IDS,
  common: COMMON_TEST_IDS,
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Generate a test ID with dynamic parts
 * @param {string} base - Base test ID
 * @param {...string} parts - Additional parts to append
 * @returns {string} Combined test ID
 */
export const makeTestId = (base, ...parts) => {
  return [base, ...parts].filter(Boolean).join('-');
};

/**
 * Check if running in test mode (dev mode or explicit test mode)
 * @returns {boolean}
 */
export const isTestMode = () => {
  if (typeof window === 'undefined') return false;
  
  // Check for dev mode user
  const devModeUser = localStorage.getItem('devModeUser');
  if (devModeUser) return true;
  
  // Check environment variable
  if (process.env.REACT_APP_TEST_MODE === 'true') return true;
  
  // Check URL parameter
  const params = new URLSearchParams(window.location.search);
  if (params.get('testMode') === 'true') return true;
  
  // Check localhost
  const isLocalhost = 
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.startsWith('192.168.');
  
  return isLocalhost;
};

/**
 * Get the data-testid prop object for a component
 * @param {string} testId - The test ID
 * @returns {Object} Props object with data-testid
 */
export const testIdProp = (testId) => ({ 'data-testid': testId });

export default TEST_IDS;

